import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CContainer,
  CRow,
  CCol,
  CCard,
  CCardBody,
  CCardHeader,
  CButton,
  CSpinner,
  CAlert,
  CTable,
  CFormInput,
  CInputGroup,
  CBadge,
  CPagination,
  CPaginationItem,
  CDropdown,
  CDropdownToggle,
  CDropdownMenu,
  CDropdownItem,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { 
  cilUser, 
  cilMagnifyingGlass, 
  cilFilter, 
  cilReload,
  cilArrowRight,
  cilArrowLeft,
  cilPeople,
  cilPlus,
  cilPencil,
  cilTrash,
  cilOptions,
  cilInfo,
  cilCart
} from '@coreui/icons';
import { superAdminAPI } from '../services/api';
import ClientModal from '../components/ClientModal';

const SuperAdminClients = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Use a ref to keep track of the client to delete
  const clientToDeleteRef = useRef(null);

  // Helper function to extract value from MongoDB extended JSON format
  const extractValue = (value) => {
    if (!value) return null;
    
    if (typeof value === 'object' && value !== null) {
      if (value.$numberDouble) {
        return value.$numberDouble;
      }
      if (value.$numberInt) {
        return value.$numberInt;
      }
      if (value.$numberLong) {
        return value.$numberLong;
      }
      return Object.values(value)[0] || value;
    }
    
    return value;
  };

  const extractSortValue = (value) => {
    const extracted = extractValue(value);
    return extracted || '';
  };

  // Format phone number
  const formatPhone = (phone) => {
    const phoneStr = extractValue(phone);
    if (!phoneStr) return 'N/A';
    
    const str = phoneStr.toString();
    const cleaned = str.replace(/\D/g, '');
    
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0,3)}) ${cleaned.slice(3,6)}-${cleaned.slice(6)}`;
    }
    
    if (cleaned.length === 11) {
      return `+${cleaned.charAt(0)} (${cleaned.slice(1,4)}) ${cleaned.slice(4,7)}-${cleaned.slice(7)}`;
    }
    
    return str;
  };

  const renderField = (value) => {
    const extracted = extractValue(value);
    if (!extracted) return 'N/A';
    return extracted;
  };

  // Fetch all clients
  const fetchClients = async () => {
    setLoading(true);
    setError('');
    setSuccessMessage('');
    try {
      const response = await superAdminAPI.getClients();
      if (response.data.success) {
        const processedClients = response.data.clients.map(client => ({
          ...client,
          billing_phone: extractValue(client.billing_phone),
          resellerid: extractValue(client.resellerid)
        }));
        setClients(processedClients);
      } else {
        setError('Failed to fetch clients');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch clients');
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchClients();
  }, []);

  // Handle create client
  const handleCreateClient = async (clientData) => {
    setActionLoading(true);
    try {
      const response = await superAdminAPI.createClient(clientData);
      if (response.data.success) {
        setSuccessMessage('Client created successfully!');
        fetchClients(); // Refresh the list
        return true;
      } else {
        throw new Error(response.data.error || 'Failed to create client');
      }
    } catch (error) {
      throw error;
    } finally {
      setActionLoading(false);
    }
  };

  // Handle update client
  const handleUpdateClient = async (clientData) => {
    setActionLoading(true);
    try {
      const response = await superAdminAPI.updateClient(selectedClient._id, clientData);
      if (response.data.success) {
        setSuccessMessage('Client updated successfully!');
        fetchClients(); // Refresh the list
        return true;
      } else {
        throw new Error(response.data.error || 'Failed to update client');
      }
    } catch (error) {
      throw error;
    } finally {
      setActionLoading(false);
    }
  };

  // Handle delete client - FIXED VERSION
  const handleDeleteClient = async () => {
    
    // Use the ref value if state is undefined
    const clientToDelete = selectedClient || clientToDeleteRef.current;
    
    if (!clientToDelete || !clientToDelete._id) {
      setError('No client selected for deletion');
      setActionLoading(false);
      return;
    }
    
    setActionLoading(true);
    setError('');
    
    try {
      const response = await superAdminAPI.deleteClient(clientToDelete._id);
      
      if (response.data.success) {
        setSuccessMessage('Client deleted successfully!');
        
        // Remove the deleted client from the local state immediately
        setClients(prevClients => prevClients.filter(client => client._id !== clientToDelete._id));
        
        setShowDeleteModal(false);
        setSelectedClient(null);
        clientToDeleteRef.current = null;
      } else {
        setError(response.data.error || 'Failed to delete client');
      }
    } catch (err) {
      
      // Detailed error handling
      if (err.response) {
        if (err.response.status === 404) {
          setError('Client not found. It may have already been deleted.');
        } else if (err.response.status === 403) {
          setError('You do not have permission to delete clients.');
        } else if (err.response.status === 401) {
          setError('Session expired. Please login again.');
        } else if (err.response.data?.error) {
          setError(err.response.data.error);
        } else {
          setError(`Server error: ${err.response.status}`);
        }
      } else if (err.request) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError('Failed to delete client: ' + err.message);
      }
    } finally {
      setActionLoading(false);
    }
  };

  // Open edit modal
  const openEditModal = async (client) => {
    setSelectedClient(client);
    try {
      const response = await superAdminAPI.getSingleClient(client._id);
      if (response.data.success) {
        setSelectedClient(response.data.client);
        setShowEditModal(true);
      }
    } catch (err) {
      setError('Failed to fetch client details');
    }
  };

  // Open view modal
  const openViewModal = async (client) => {
    setSelectedClient(client);
    try {
      const response = await superAdminAPI.getSingleClient(client._id);
      if (response.data.success) {
        setSelectedClient(response.data.client);
        setShowViewModal(true);
      }
    } catch (err) {
      setError('Failed to fetch client details');
    }
  };

  // Open delete confirmation modal - FIXED VERSION
  const openDeleteModal = (client) => {
    
    // Set both state and ref to ensure we have the client data
    setSelectedClient(client);
    clientToDeleteRef.current = client;
    setShowDeleteModal(true);
  };

// In SuperAdminClients component - handleAddToCart function
const handleAddToCart = (client) => {
  navigate(`/client/${client._id}/products`, {
    state: { 
      client: client,
      isSuperAdmin: true,
      // Pass the reseller ID from the client for super admin
      resellerId: client.resellerid ? renderField(client.resellerid) : null
    }
  });
};

  // Filter clients based on search term
  const filteredClients = clients.filter(client => {
    const searchLower = searchTerm.toLowerCase();
    
    const safeToString = (value) => {
      if (value == null) return '';
      if (typeof value === 'string') return value;
      return String(value);
    };

    return (
      safeToString(client.name).toLowerCase().includes(searchLower) ||
      safeToString(client.resellername).toLowerCase().includes(searchLower) ||
      safeToString(client.client_owner_email).toLowerCase().includes(searchLower) ||
      safeToString(client.client_owner_email_2).toLowerCase().includes(searchLower) ||
      safeToString(client.reseller_contact_email).toLowerCase().includes(searchLower) ||
      safeToString(client.billing_city).toLowerCase().includes(searchLower) ||
      safeToString(client.clientid).toLowerCase().includes(searchLower) ||
      safeToString(client.resellerid).toLowerCase().includes(searchLower)
    );
  });

  // Sort clients
  const sortedClients = [...filteredClients].sort((a, b) => {
    if (sortConfig.key) {
      const aValue = extractSortValue(a[sortConfig.key]);
      const bValue = extractSortValue(b[sortConfig.key]);
      
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
    }
    return 0;
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentClients = sortedClients.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedClients.length / itemsPerPage);

  // Handle sort
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Handle page change
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Go back to dashboard
  const goBack = () => {
    navigate('/superadmin/dashboard');
  };

  return (
    <div>
      {/* Header with back button */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <CButton 
            color="secondary" 
            variant="outline" 
            className="me-3"
            onClick={goBack}
          >
            ← Back to Dashboard
          </CButton>
          <div>
            <h2>
              <CIcon icon={cilPeople} className="me-2 text-success" />
              All Clients
            </h2>
            <p className="text-medium-emphasis">
              Manage all client accounts ({clients.length} total)
            </p>
          </div>
        </div>
        <div className="d-flex gap-2">
          <CButton color="success" onClick={() => setShowAddModal(true)}>
            <CIcon icon={cilPlus} className="me-2" />
            Add New Client
          </CButton>
          <CButton color="secondary" onClick={fetchClients}>
            <CIcon icon={cilReload} className="me-2" />
            Refresh
          </CButton>
        </div>
      </div>

      {/* Success Alert */}
      {successMessage && (
        <CAlert color="success" className="mb-3" onClose={() => setSuccessMessage('')} dismissible>
          {successMessage}
        </CAlert>
      )}

      {/* Error Alert */}
      {error && (
        <CAlert color="danger" className="mb-3" onClose={() => setError('')} dismissible>
          {error}
        </CAlert>
      )}

      {/* Search and Filters */}
      <CCard className="mb-4">
        <CCardBody>
          <CRow className="align-items-center">
            <CCol md={6}>
              <CInputGroup>
                <CFormInput
                  placeholder="Search clients by name, reseller, email, or location..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
                <CButton color="success" type="button">
                  <CIcon icon={cilMagnifyingGlass} />
                </CButton>
              </CInputGroup>
            </CCol>
            <CCol md={6} className="text-end">
              <span className="text-medium-emphasis me-3">
                Showing {currentClients.length} of {filteredClients.length} clients
              </span>
              <CButton color="secondary" variant="outline">
                <CIcon icon={cilFilter} className="me-2" />
                Filters
              </CButton>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>

      {/* Loading State */}
      {loading ? (
        <div className="text-center py-5">
          <CSpinner color="success" />
          <p className="mt-2">Loading clients...</p>
        </div>
      ) : (
        <>
          {/* Clients Table - ORIGINAL STRUCTURE KEPT */}
          <CCard>
            <CCardHeader>
              <h5>Client Directory</h5>
            </CCardHeader>
            <CCardBody>
              {currentClients.length === 0 ? (
                <div className="text-center py-5">
                  <p className="text-medium-emphasis">No clients found</p>
                  {searchTerm && (
                    <CButton 
                      color="link" 
                      onClick={() => setSearchTerm('')}
                    >
                      Clear search
                    </CButton>
                  )}
                </div>
              ) : (
                <>
                  <CTable striped hover responsive>
                    <thead>
                      <tr>
                        <th>Client ID</th>
                        <th>Client Name</th>
                        <th>Reseller</th>
                        <th>Customer Type</th>
                        <th>Primary Email</th>
                        <th>Location</th>
                        <th>Phone</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentClients.map(client => (
                        <tr key={client._id}>
                          <td>
                            <CBadge color="success" style={{ cursor: 'pointer' }} onClick={() => openViewModal(client)}>
                              {renderField(client.clientid)?.substring(0, 8) || 'N/A'}...
                            </CBadge>
                          </td>
                          <td>
                            <strong>{renderField(client.name) || 'N/A'}</strong>
                            {client.resellerid && (
                              <div className="small text-medium-emphasis">
                                Reseller ID: {renderField(client.resellerid)}
                              </div>
                            )}
                          </td>
                          <td>
                            <div>
                              {renderField(client.resellername) || 'N/A'}
                              {client.reseller_contact_full_name && (
                                <div className="small text-medium-emphasis">
                                  Contact: {renderField(client.reseller_contact_full_name)}
                                </div>
                              )}
                            </div>
                          </td>
                          <td>
                            <CBadge color={client.custmertype === 'Distributor' ? 'info' : 'secondary'}>
                              {renderField(client.custmertype) || 'N/A'}
                            </CBadge>
                          </td>
                          <td>
                            {client.client_owner_email ? (
                              <a href={`mailto:${renderField(client.client_owner_email)}`} className="text-decoration-none">
                                {renderField(client.client_owner_email)}
                              </a>
                            ) : (
                              'N/A'
                            )}
                          </td>
                          <td>
                            <div>
                              <div>{renderField(client.billing_city) || 'N/A'}, {renderField(client['billing_state/provience']) || ''}</div>
                              <div className="small text-medium-emphasis">
                                {renderField(client.billing_country) || ''}
                              </div>
                            </div>
                          </td>
                          <td>
                            {formatPhone(client.billing_phone)}
                          </td>
                          <td>
                            <div className="d-flex gap-2 align-items-center">
                              {/* Dropdown for View, Edit, Delete */}
                              <CDropdown>
                                <CDropdownToggle color="secondary" size="sm" variant="ghost">
                                  <CIcon icon={cilOptions} />
                                </CDropdownToggle>
                                <CDropdownMenu>
                                  <CDropdownItem onClick={() => openViewModal(client)}>
                                    <CIcon icon={cilInfo} className="me-2" />
                                    View Details
                                  </CDropdownItem>
                                  <CDropdownItem onClick={() => openEditModal(client)}>
                                    <CIcon icon={cilPencil} className="me-2" />
                                    Edit
                                  </CDropdownItem>
                                  <CDropdownItem 
                                    onClick={() => openDeleteModal(client)}
                                    className="text-danger"
                                  >
                                    <CIcon icon={cilTrash} className="me-2" />
                                    Delete
                                  </CDropdownItem>
                                </CDropdownMenu>
                              </CDropdown>
                              
                              {/* Standalone Add to Cart Button - EXACTLY like reseller code */}
                              <CButton 
                                color="primary" 
                                size="sm" 
                                onClick={() => handleAddToCart(client)}
                                title="Add Products to Client"
                              >
                                <CIcon icon={cilCart} className="me-1" />
                                Add Products
                              </CButton>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </CTable>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-center mt-4">
                      <CPagination>
                        <CPaginationItem 
                          aria-label="Previous" 
                          disabled={currentPage === 1}
                          onClick={() => currentPage > 1 && paginate(currentPage - 1)}
                          style={{ cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
                        >
                          <CIcon icon={cilArrowLeft} />
                        </CPaginationItem>
                        
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNumber;
                          if (totalPages <= 5) {
                            pageNumber = i + 1;
                          } else if (currentPage <= 3) {
                            pageNumber = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNumber = totalPages - 4 + i;
                          } else {
                            pageNumber = currentPage - 2 + i;
                          }
                          
                          return (
                            <CPaginationItem
                              key={pageNumber}
                              active={currentPage === pageNumber}
                              onClick={() => paginate(pageNumber)}
                              style={{ cursor: 'pointer' }}
                            >
                              {pageNumber}
                            </CPaginationItem>
                          );
                        })}
                        
                        <CPaginationItem 
                          aria-label="Next" 
                          disabled={currentPage === totalPages}
                          onClick={() => currentPage < totalPages && paginate(currentPage + 1)}
                          style={{ cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
                        >
                          <CIcon icon={cilArrowRight} />
                        </CPaginationItem>
                      </CPagination>
                    </div>
                  )}

                  {/* Page Info */}
                  <div className="text-center mt-3">
                    <p className="text-medium-emphasis">
                      Page {currentPage} of {totalPages} • Showing {currentClients.length} clients per page
                    </p>
                  </div>
                </>
              )}
            </CCardBody>
          </CCard>
        </>
      )}

      {/* Add Client Modal */}
      <ClientModal
        show={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleCreateClient}
        mode="create"
      />

      {/* Edit Client Modal */}
      <ClientModal
        show={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedClient(null);
        }}
        onSave={handleUpdateClient}
        client={selectedClient}
        mode="edit"
      />

      {/* Delete Confirmation Modal - IMPROVED VERSION */}
      <CModal 
        visible={showDeleteModal} 
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedClient(null);
          clientToDeleteRef.current = null;
        }}
      >
        <CModalHeader>
          <CModalTitle>Confirm Delete</CModalTitle>
        </CModalHeader>
        <CModalBody>
          {(selectedClient || clientToDeleteRef.current) ? (
            <>
              <p>Are you sure you want to delete the following client?</p>
              <div className="alert alert-danger">
                <strong>{renderField(selectedClient?.name || clientToDeleteRef.current?.name)}</strong><br />
                Client ID: {renderField(selectedClient?.clientid || clientToDeleteRef.current?.clientid)}<br />
                Database ID: <small className="text-muted">{selectedClient?._id || clientToDeleteRef.current?._id}</small><br />
                Email: {renderField(selectedClient?.client_owner_email || clientToDeleteRef.current?.client_owner_email)}<br />
                Reseller: {renderField(selectedClient?.resellername || clientToDeleteRef.current?.resellername)}
              </div>
              <p className="text-danger">
                <strong>Warning:</strong> This action cannot be undone. 
                All client data will be permanently deleted.
              </p>
            </>
          ) : (
            <div className="alert alert-warning">
              <p>Client information not available. Please try again.</p>
            </div>
          )}
        </CModalBody>
        <CModalFooter>
          <CButton 
            color="secondary" 
            onClick={() => {
              setShowDeleteModal(false);
              setSelectedClient(null);
              clientToDeleteRef.current = null;
            }}
            disabled={actionLoading}
          >
            Cancel
          </CButton>
          <CButton 
            color="danger" 
            onClick={handleDeleteClient}
            disabled={actionLoading || (!selectedClient && !clientToDeleteRef.current)}
            title={(!selectedClient && !clientToDeleteRef.current) ? "No client selected" : ""}
          >
            {actionLoading ? (
              <>
                <CSpinner size="sm" className="me-2" />
                Deleting...
              </>
            ) : 'Delete Client'}
          </CButton>
        </CModalFooter>
      </CModal>

      {/* View Client Details Modal - ADDED Add Products button */}
      <CModal 
        visible={showViewModal} 
        onClose={() => {
          setShowViewModal(false);
          setSelectedClient(null);
        }}
        size="lg"
      >
        <CModalHeader>
          <CModalTitle>Client Details</CModalTitle>
        </CModalHeader>
        <CModalBody>
          {selectedClient && (
            <div>
              <h5 className="mb-3">Client Information</h5>
              <CRow className="mb-2">
                <CCol md={6}>
                  <strong>Client Name:</strong>
                  <p>{renderField(selectedClient.name)}</p>
                </CCol>
                <CCol md={6}>
                  <strong>Customer Type:</strong>
                  <p>
                    <CBadge color={selectedClient.custmertype === 'Distributor' ? 'info' : 'secondary'}>
                      {renderField(selectedClient.custmertype)}
                    </CBadge>
                  </p>
                </CCol>
              </CRow>
              
              <CRow className="mb-2">
                <CCol md={6}>
                  <strong>Reseller ID:</strong>
                  <p>{renderField(selectedClient.resellerid)}</p>
                </CCol>
                <CCol md={6}>
                  <strong>Reseller Name:</strong>
                  <p>{renderField(selectedClient.resellername)}</p>
                </CCol>
              </CRow>
              
              <h5 className="mb-3 mt-4">Contact Information</h5>
              <CRow className="mb-2">
                <CCol md={6}>
                  <strong>Primary Email:</strong>
                  <p>
                    <a href={`mailto:${renderField(selectedClient.client_owner_email)}`}>
                      {renderField(selectedClient.client_owner_email)}
                    </a>
                  </p>
                </CCol>
                <CCol md={6}>
                  <strong>Secondary Email:</strong>
                  <p>
                    {selectedClient.client_owner_email_2 ? (
                      <a href={`mailto:${renderField(selectedClient.client_owner_email_2)}`}>
                        {renderField(selectedClient.client_owner_email_2)}
                      </a>
                    ) : 'N/A'}
                  </p>
                </CCol>
              </CRow>
              
              <CRow className="mb-2">
                <CCol md={6}>
                  <strong>Reseller Contact:</strong>
                  <p>{renderField(selectedClient.reseller_contact_full_name) || 'N/A'}</p>
                </CCol>
                <CCol md={6}>
                  <strong>Reseller Contact Email:</strong>
                  <p>
                    {selectedClient.reseller_contact_email ? (
                      <a href={`mailto:${renderField(selectedClient.reseller_contact_email)}`}>
                        {renderField(selectedClient.reseller_contact_email)}
                      </a>
                    ) : 'N/A'}
                  </p>
                </CCol>
              </CRow>
              
              <h5 className="mb-3 mt-4">Billing Address</h5>
              <CRow className="mb-2">
                <CCol md={12}>
                  <strong>Street Address:</strong>
                  <p>{renderField(selectedClient['billiing_street_(end_user)'])}</p>
                </CCol>
              </CRow>
              
              <CRow className="mb-2">
                <CCol md={4}>
                  <strong>City:</strong>
                  <p>{renderField(selectedClient.billing_city)}</p>
                </CCol>
                <CCol md={4}>
                  <strong>State/Province:</strong>
                  <p>{renderField(selectedClient['billing_state/provience'])}</p>
                </CCol>
                <CCol md={4}>
                  <strong>Zip/Postal Code:</strong>
                  <p>{renderField(selectedClient.billingzipcode)}</p>
                </CCol>
              </CRow>
              
              <CRow className="mb-2">
                <CCol md={6}>
                  <strong>Country:</strong>
                  <p>{renderField(selectedClient.billing_country)}</p>
                </CCol>
                <CCol md={6}>
                  <strong>Phone:</strong>
                  <p>{formatPhone(selectedClient.billing_phone)}</p>
                </CCol>
              </CRow>
              
              <div className="mt-4 pt-3 border-top">
                <small className="text-medium-emphasis">
                  Client ID: {renderField(selectedClient.clientid)}<br />
                  Created: {selectedClient.created_at ? new Date(selectedClient.created_at).toLocaleDateString() : 'N/A'}<br />
                  Last Updated: {selectedClient.updated_at ? new Date(selectedClient.updated_at).toLocaleDateString() : 'N/A'}
                </small>
              </div>
            </div>
          )}
        </CModalBody>
        <CModalFooter>
          <CButton 
            color="secondary" 
            onClick={() => {
              setShowViewModal(false);
              setSelectedClient(null);
            }}
          >
            Close
          </CButton>
          <CButton 
            color="primary" 
            onClick={() => {
              setShowViewModal(false);
              openEditModal(selectedClient);
            }}
          >
            Edit Client
          </CButton>
          <CButton 
            color="success"
            onClick={() => {
              setShowViewModal(false);
              handleAddToCart(selectedClient);
            }}
          >
            <CIcon icon={cilCart} className="me-2" />
            Add Products
          </CButton>
          <CButton 
            color="danger" 
            onClick={() => {
              setShowViewModal(false);
              openDeleteModal(selectedClient);
            }}
          >
            Delete Client
          </CButton>
        </CModalFooter>
      </CModal>
    </div>
  );
};

export default SuperAdminClients;